// Fit the game to its area (Review 17, C61). The preview frame is drawn at a "design size" (the
// area divided by the scale) and scaled down with a CSS transform, so the student's whole page shows
// without scrolling. The bridge reports the page's size from inside the frame (BridgeMessage
// "size"); each report moves the scale toward the one where the content just fits.
//
// fitStep is self-contained (no outside names) so the public show page can inline its source.

/** What the bridge measures inside the frame: content width/height and the frame's viewport. */
export interface FitMeasure {
  /** documentElement.scrollWidth: equals vw unless something is wider than the frame. */
  w: number;
  /** The bottom of the page's content; can be less than vh. */
  h: number;
  vw: number;
  vh: number;
}

export interface FitState {
  scale: number;
  /** The largest scale allowed (1, or more in Play big; lowered when the page overflowed sideways). */
  ceiling: number;
  /** The page grows with its window (a long website, height: 100vh plus padding), so fitting would only shrink it: show it at 100% and let it scroll. */
  scroll: boolean;
  /** Overflow below the window at the last report, and the scale it was measured at. */
  lastOverflow: number;
  lastAt: number;
  steps: number;
}

/** Below this the page is a long document, not a game: it scrolls at 100% instead. */
export const FIT_MIN = 0.4;

export function fitStart(max = 1, scale = 1): FitState {
  return { scale: Math.min(scale, max), ceiling: max, scroll: false, lastOverflow: -1, lastAt: 0, steps: 0 };
}

/** One report from the frame → the next state. `box` is the area the game is shown in. */
export function fitStep(state: FitState, m: FitMeasure, box: { w: number; h: number }): FitState {
  var min = 0.4; // FIT_MIN, inlined so the function can be copied into the show page as text
  if (state.scroll || state.steps > 12) return state;
  if (!(box.w > 0 && box.h > 0 && m.vw > 0 && m.vh > 0 && m.w > 0 && m.h > 0)) return state;
  var at = box.w / m.vw; // the scale this report was measured at
  var overX = m.w > m.vw + 1;
  var overflow = m.h > m.vh + 1 ? m.h - m.vh : 0;
  var ceiling = state.ceiling;
  // It was too wide at this scale, so never go back up to it (stops see-sawing on wide pages).
  if (overX) ceiling = Math.min(ceiling, at - 0.005);
  // Overflowing by the same amount at two different scales: the page grows with its window.
  if (overflow > 0 && state.lastOverflow > 0 && Math.abs(overflow - state.lastOverflow) <= 2 && Math.abs(at - state.lastAt) > 0.01) {
    return { scale: 1, ceiling: ceiling, scroll: true, lastOverflow: overflow, lastAt: at, steps: state.steps + 1 };
  }
  var next = Math.min(ceiling, box.h / m.h, overX ? box.w / m.w : Infinity);
  if (next < min) return { scale: 1, ceiling: ceiling, scroll: true, lastOverflow: overflow, lastAt: at, steps: state.steps + 1 };
  if (Math.abs(next - state.scale) < 0.01) next = state.scale; // no jitter from rounding
  return { scale: next, ceiling: ceiling, scroll: false, lastOverflow: overflow, lastAt: at, steps: next === state.scale ? state.steps : state.steps + 1 };
}

/** The frame's style for a scale: the design size, scaled from the top-left corner. */
export function fitFrameSize(scale: number, box: { w: number; h: number }): { width: number; height: number } {
  return { width: box.w / scale, height: box.h / scale };
}
